import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";

function CarBox({ data }) {
  const [carLoad, setCarLoad] = useState(true);
  const { t } = useTranslation();
  const car = data;

  return (
    <div className="w-full">
      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-slate-100 hover:shadow-[0_20px_60px_-15px_rgba(245,39,43,0.3)] transition-all duration-500">
        <div className="grid grid-cols-1 gap-0 lg:grid-cols-5">
          {/* Car Image Section */}
          <div className="relative p-8 lg:col-span-3 bg-gradient-to-br from-slate-50 to-slate-100 md:p-12">
            {carLoad && (
              <div className="absolute inset-0 flex items-center justify-center bg-slate-100">
                <div className="relative">
                  <div className="w-16 h-16 border-4 border-slate-300 border-t-[#f5272b] rounded-full animate-spin"></div>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <i className="fa-solid fa-car text-[#f5272b] text-xl"></i>
                  </div>
                </div>
              </div>
            )}

            <div
              className={`relative transition-opacity duration-500 ${carLoad ? "opacity-0" : "opacity-100"}`}
              style={{ display: carLoad ? "none" : "block" }}
            >
              <div className="relative">
                {/* Decorative background */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#f5272b]/5 to-transparent rounded-2xl"></div>

                <Carousel
                  interval={3000}
                  showArrows={true}
                  showThumbs={false}
                  infiniteLoop
                  stopOnHover={true}
                  autoPlay
                  showStatus={false}
                  renderArrowPrev={(onClickHandler, hasPrev) =>
                    hasPrev && (
                      <button
                        type="button"
                        onClick={onClickHandler}
                        className="absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-[#f5272b] text-slate-800 hover:text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 group"
                      >
                        <i className="text-lg fa-solid fa-chevron-left"></i>
                      </button>
                    )
                  }
                  renderArrowNext={(onClickHandler, hasNext) =>
                    hasNext && (
                      <button
                        type="button"
                        onClick={onClickHandler}
                        className="absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-[#f5272b] text-slate-800 hover:text-white rounded-full shadow-lg flex items-center justify-center transition-all duration-300 group"
                      >
                        <i className="text-lg fa-solid fa-chevron-right"></i>
                      </button>
                    )
                  }
                  renderIndicator={(onClickHandler, isSelected, index, label) => {
                    return (
                      <button
                        type="button"
                        onClick={onClickHandler}
                        className={`inline-block mx-1.5 w-2.5 h-2.5 rounded-full transition-all duration-300 ${
                          isSelected ? "bg-[#f5272b] w-8" : "bg-slate-300 hover:bg-slate-400"
                        }`}
                        aria-label={`${label} ${index + 1}`}
                      />
                    );
                  }}
                >
                  {car.image_urls?.map((item, idx) => (
                    <div key={idx} className="px-4 pb-8">
                      <img
                        src={item}
                        alt={`${car.name} view ${idx + 1}`}
                        className="object-contain w-full h-auto rounded-xl"
                        onLoad={() => setCarLoad(false)}
                      />
                    </div>
                  ))}
                </Carousel>
              </div>
            </div>
          </div>

          {/* Car Details Section */}
          <div className="flex flex-col p-8 lg:col-span-2 md:p-10">
            {/* Header */}
            <div className="mb-8">
              <div className="inline-block px-4 py-1.5 bg-[#f5272b]/10 text-[#f5272b] font-semibold text-sm rounded-full mb-3">
                {car.brands}
              </div>
              <h3 className="text-3xl font-bold leading-tight md:text-4xl text-slate-900">{car.name}</h3>
            </div>

            {/* Specifications Grid */}
            <div className="flex-1 mb-8 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                {/* Brand */}
                <div className="p-4 border bg-slate-50 rounded-xl border-slate-200">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                      <i className="fa-solid fa-tag text-[#f5272b] text-lg"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="mb-1 text-xs font-medium text-slate-500">{t("brand")}</p>
                      <p className="text-sm font-semibold truncate text-slate-900">{car.brands}</p>
                    </div>
                  </div>
                </div>

                {/* AC */}
                <div className="p-4 border bg-slate-50 rounded-xl border-slate-200">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                      <i className="fa-solid fa-snowflake text-[#f5272b] text-lg"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="mb-1 text-xs font-medium text-slate-500">{t("ac")}</p>
                      <p className="text-sm font-semibold text-slate-900">Yes</p>
                    </div>
                  </div>
                </div>

                {/* Transmission */}
                <div className="p-4 border bg-slate-50 rounded-xl border-slate-200">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                      <i className="fa-solid fa-gears text-[#f5272b] text-lg"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="mb-1 text-xs font-medium text-slate-500">{t("transmission")}</p>
                      <p className="text-sm font-semibold truncate text-slate-900">{car.transmision}</p>
                    </div>
                  </div>
                </div>

                {/* Color */}
                <div className="p-4 border bg-slate-50 rounded-xl border-slate-200">
                  <div className="flex items-start gap-3">
                    <div className="flex items-center justify-center flex-shrink-0 w-10 h-10 bg-white rounded-lg shadow-sm">
                      <i className="fa-solid fa-palette text-[#f5272b] text-lg"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="mb-1 text-xs font-medium text-slate-500">{t("color")}</p>
                      <p className="text-sm font-semibold truncate text-slate-900">{car.color}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CTA Button */}
            <a
              onClick={(e) => {
                e.preventDefault();
                const target = document.querySelector("#booking-section");
                if (target) {
                  target.scrollIntoView({ behavior: "smooth" });
                }
              }}
              href="#booking-section"
              className="block w-full text-center px-8 py-4 bg-[#f5272b] hover:bg-red-700 text-white font-bold text-lg rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group"
            >
              <span className="flex items-center justify-center gap-3">
                {t("reserve_now")}
                <i className="text-lg transition-transform fa-solid fa-arrow-right group-hover:translate-x-1"></i>
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CarBox;
